import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionCard } from "@/components/shared/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${today().slice(0, 8)}01`;
const ALL = "__all__";

// Completed business outcomes only: one booking row per new booking / move, one applied extension request per extension. Dates are UTC.
export function StaffActivityPanel() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [agent, setAgent] = useState(ALL);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!from || !to) return;
    api.get("/workflow/activity", { params: { from, to } }).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(e.response?.data?.detail || "Could not load activity"));
  }, [from, to]);
  const rows = (data?.agents || []).filter((a) => agent === ALL || a.agent_id === agent);
  const KEYS = ["new_bookings", "moves", "extensions", "cancellations", "amendments", "total"];
  const totals = KEYS.reduce((acc, k) => ({ ...acc, [k]: rows.reduce((s, r) => s + (r[k] || 0), 0) }), {});
  const download = async () => {
    setBusy(true);
    try {
      const params = { from, to }; if (agent !== ALL) params.agent_id = agent;
      const r = await api.get("/workflow/activity.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `staff-activity_${from}_${to}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Activity CSV downloaded (staff only)");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not export activity"); }
    finally { setBusy(false); }
  };
  return (
    <SectionCard title="Staff activity" subtitle="Completed outcomes by agent — new bookings, extensions and moves each counted once (UTC dates)" testId="wf-activity"
      action={<Button size="sm" variant="outline" data-testid="wf-activity-export" onClick={download} disabled={busy || !data}><Download className="mr-1 h-3.5 w-3.5" /> CSV</Button>}>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label htmlFor="wf-act-from">From</Label><Input id="wf-act-from" data-testid="wf-activity-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[11rem]" /></div>
        <div className="space-y-1"><Label htmlFor="wf-act-to">To</Label><Input id="wf-act-to" data-testid="wf-activity-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[11rem]" /></div>
        <div className="space-y-1"><Label>Agent</Label>
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger data-testid="wf-activity-agent" className="h-9 w-[12rem]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All agents</SelectItem>{(data?.agents || []).map((a) => <SelectItem key={a.agent_id} value={a.agent_id}>{a.agent_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {error && <p className="text-sm text-jigsaw-danger" data-testid="wf-activity-error">{error}</p>}
      {data && (
        <div className="overflow-x-auto">
        <Table data-testid="wf-activity-table">
          <TableHeader><TableRow><TableHead>Agent</TableHead><TableHead className="text-right">New bookings</TableHead><TableHead className="text-right">Moves</TableHead><TableHead className="text-right">Extensions</TableHead><TableHead className="text-right">Cancellations</TableHead><TableHead className="text-right">Other amendments</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-jigsaw-muted">No completed outcomes in this range.</TableCell></TableRow>}
            {rows.map((a) => (
              <TableRow key={a.agent_id} data-testid={`wf-activity-row-${a.agent_id}`}>
                <TableCell className="font-medium">{a.agent_name}</TableCell>{KEYS.map((k) => <TableCell key={k} className={`text-right ${k === "total" ? "font-semibold" : ""}`}>{a[k] || 0}</TableCell>)}
              </TableRow>
            ))}
            <TableRow data-testid="wf-activity-totals"><TableCell className="font-semibold">All agents{agent !== ALL ? " (filtered)" : ""}</TableCell>{KEYS.map((k) => <TableCell key={k} className="text-right font-semibold">{totals[k]}</TableCell>)}</TableRow>
          </TableBody>
        </Table>
        <p className="mt-2 text-[12px] text-jigsaw-muted" data-testid="wf-activity-definitions">Counting (UTC): a booking created in the range counts once as a new booking or a move (by who completed it); an extension, cancellation or other amendment counts once when its change request is APPLIED (dated by when it was applied, by who applied it). Extensions are classified separately from other amendments. Pending, rejected, blocked and failed changes, retries, recovery runs and repeated exports never add a count. Internal metric — not shown to clients.</p>
        </div>
      )}
    </SectionCard>
  );
}
